import { Injectable } from "@angular/core";
import { ConnectServerService } from "../../../../../../../services/connect-server.service";
import { UserState } from "../../../../../../../storage-ngrx/user/user.reducer";
import { Store } from "@ngrx/store";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { dateRangeValidator } from "../validators/date-validator";
import { timeRangeValidator } from "../validators/time-validator";
import { combineLatest, filter, map, Observable, take } from "rxjs";
import { customerValidator } from "../validators/customer-validator";
import { ApiResponse } from "../../../../../../../interfaces/api-response";
import { EventBackendData } from "../../interfaces/event-backend-data";


export interface EventFormData {
  idevent: number;
  event_type: string | null;
  idcourse: number | null;
  title: string | null;
  description: string | null;
  internal: number;
  date_start: string | null;
  date_end: string | null;
  idroom: number | null;
  isallday: number;
  time_start: string | null;
  time_end: string | null;
  customer: any | null;
  idcontact: number | null;
  customer_headquarter: number | null;
  internal_headquarter: number | null;
  keepinformed: number[] | null;
  customerstudents: number[] | null;
}



@Injectable({
  providedIn: 'root'
})
export class CalendarEventFormService {

   readonly defaultValues: EventFormData = {
    idevent: 0,
    event_type: 'GENERIC',
    idcourse: null,
    title: null,
    description: null,
    internal: 0,
    date_start: null,
    date_end: null,
    idroom: null,
    isallday: 0,
    time_start: null,
    time_end: null,
    customer: null,
    idcontact: null,
    customer_headquarter: null,
    internal_headquarter: null,
    keepinformed: null,
    customerstudents: null
  };

  constructor(
    private connectServerService: ConnectServerService,
    private store: Store<{ user: UserState }>
  ) {}

  createEventForm(): FormGroup {
    return new FormGroup({
      idevent: new FormControl<number>(0),
      event_type: new FormControl<string | null>('', Validators.required),
      idcourse: new FormControl<number | null>(null, Validators.required),
      title: new FormControl<string | null>('', Validators.required),
      description: new FormControl<string | null>(null),
      internal: new FormControl<number>(0),
      date_start: new FormControl<string | null>(null, Validators.required),
      date_end: new FormControl<string | null>(null, Validators.required),
      idroom: new FormControl<number | null>(null, Validators.required),
      isallday: new FormControl<number>(0),
      time_start: new FormControl<string | null>(null, Validators.required),
      time_end: new FormControl<string | null>(null, Validators.required),
      customer: new FormControl<any | null>(null, [Validators.required, customerValidator()]),
      idcontact: new FormControl<number | null>(null),
      customer_headquarter: new FormControl<number | null>({ value: null, disabled: true }),
      internal_headquarter: new FormControl<number | null>(null),
      keepinformed: new FormControl<number[] | null>(null, Validators.required),
      customerstudents: new FormControl<number[] | null>(null)
    }, {
      validators: [dateRangeValidator(), timeRangeValidator()]
    });
  }

  loadEventData(idevent: number): Observable<ApiResponse<{infoEventCalendar: EventBackendData}>> {
    return this.connectServerService.getRequestCookie('agenda/eventDetails', { idevent });
  }

  saveEventData(obj: EventFormData): Observable<ApiResponse<{id: number}>>{
    return this.connectServerService.postRequestCookie('agenda/saveEventCalendar', obj );
  }

  setupFormLogic(form: FormGroup, skipInitialCustomerLogic: boolean = false): void {
    // Setup initial state
   // this.applyEventTypeLogic(form, form.get('event_type')?.value);
    // this.applyDateTypeLogic(form, form.get('event_type')?.value);
    // this.applyIsAllDayLogic(form, form.get('isallday')?.value);
    // this.applyInternalLogic(form, form.get('internal')?.value);

    // Setup listeners
    form.get('event_type')?.valueChanges.subscribe(value => {
      this.applyEventTypeLogic(form, value);
      this.applyDateTypeLogic(form, value);
    });

    form.get('isallday')?.valueChanges.subscribe(value => {
      this.applyIsAllDayLogic(form, value);
    });

    // Solo se non stiamo caricando dati iniziali, registra il listener per customer
    // if (!skipInitialCustomerLogic) {
    //   form.get('customer')?.valueChanges.subscribe(value => {
    //     this.applyCustomerLogic(form, value);
    //   });
    // }

    form.get('internal')?.valueChanges.subscribe(value => {
      this.applyInternalLogic(form, value);
    });

    form.get('date_start')?.valueChanges.subscribe(value => {
      this.applyDateLogic(form, value);
    });
  }

  private applyEventTypeLogic(form: FormGroup, eventType: string | null): void {
    // console.log('applyEventTypeLogic', eventType);
    if (eventType !== 'ROOM' && eventType !== 'COURSE') {
      form.get('idcourse')?.disable();
      form.get('idcourse')?.setValue(null);
      form.get('idroom')?.disable();
      form.get('idroom')?.setValue(null);
      form.get('title')?.enable();
    } else if (eventType === 'ROOM') {
      form.get('idcourse')?.disable();
      form.get('idcourse')?.setValue(null);
      form.get('title')?.disable();
      form.get('title')?.setValue(null);
      form.get('idroom')?.enable();
    } else if (eventType === 'COURSE') {
      form.get('title')?.disable();
      form.get('title')?.setValue(null);
      form.get('idroom')?.disable();
      form.get('idroom')?.setValue(null);
      form.get('idcourse')?.enable();
    }
  }

  private applyDateTypeLogic(form: FormGroup, eventType: string | null): void {
    if (eventType === 'REMINDER') {
      form.get('isallday')?.setValue(1);
      form.get('isallday')?.disable();
    } else {
      form.get('isallday')?.enable();
    }
  }

  private applyIsAllDayLogic(form: FormGroup, isAllDay: any): void {
    if (isAllDay == 1 || isAllDay === true) {
      form.get('time_start')?.disable();
      form.get('time_start')?.setValue(null);
      form.get('time_end')?.setValue(null);
      form.get('time_end')?.disable();
    } else {
      form.get('time_start')?.enable();
      form.get('time_end')?.enable();
    }
  }

  private applyInternalLogic(form: FormGroup, internal: number): void {
    if (internal === 0) { // External
      form.get('customer')?.enable();
      form.get('internal_headquarter')?.disable();
      form.get('internal_headquarter')?.setValue(null);
    } else if (internal === 1) { // Internal
      form.get('customer')?.disable();
      form.get('customer')?.setValue(null);
      form.get('internal_headquarter')?.enable();
      form.get('customer_headquarter')?.setValue(null);
      form.get('customer_headquarter')?.disable();
      form.get('idcontact')?.disable();
      form.get('idcontact')?.setValue(null);
    } else if (internal === 2) { // Not specified
      form.get('customer')?.disable();
      form.get('customer')?.setValue(null);
      form.get('customer_headquarter')?.setValue(null);
      form.get('customer_headquarter')?.disable();
      form.get('internal_headquarter')?.disable();
      form.get('internal_headquarter')?.setValue(null);
      form.get('idcontact')?.disable();
      form.get('idcontact')?.setValue(null);
    }
  }

  applyCustomerLogic(form: FormGroup, customer: any): void {
    if (this.isValidCustomer(customer) && form.get('internal')?.value === 0) {
      form.get('customer_headquarter')?.enable();
    } else {
      form.get('customer_headquarter')?.setValue(null);
      form.get('customer_headquarter')?.disable();
    }
  }

  private applyDateLogic(form: FormGroup, startDate: any): void {
    if (startDate && form.get('date_end')?.value == null) {
      form.get('date_end')?.setValue(startDate);
    }
  }

  private isValidCustomer(customer: any): boolean {
    // console.log('isValidCustomer', customer);
    return customer && typeof customer === 'object' && 'idregistry' in customer;
  }

  getCalendarTypes(types: string[]): Observable<any[]> {
    return this.connectServerService.getRequestCookie('agenda/calendarTypes', { types });
  }

  getHeadquarters(): Observable<any[]> {
    return this.connectServerService.getRequestCookie('company/headquartersList', {});
  }

  getCourses(): Observable<any[]> {
    return this.connectServerService.getRequestCookie('training/coursesList', { active: 1 });
  }

  getUsersList(): Observable<any[]> {
    return this.connectServerService.getRequestCookie('user/usersListNoAdmin', {});
  }

  getCurrentUserId(): Observable<number | null> {
    return this.store.select(state => state.user.userInfo).pipe(
      map(user => user?.id || null)
    );
  }

  getCustomerLocationContacts(customerId: number): Observable<any> {
    return this.connectServerService.getRequestCookie('customer/customerLocationContactsStudents', {
      idregistry: customerId
    });
  }

  setupUserSelection(form: FormGroup, usersList$: Observable<any[]>): void {
    combineLatest([
      usersList$,
      this.getCurrentUserId()
    ]).pipe(
      take(1),
      filter(([usersList, currentUserId]) => !!usersList && !!currentUserId)
    ).subscribe(([usersList, currentUserId]) => {
      const userExists = usersList.some(user => user.id === currentUserId);
      if (userExists) {
        form.get('keepinformed')?.setValue([currentUserId]);
      }
    });
  }

  // Metodo per applicare i dati quando si carica un evento esistente
  applyEventData(form: FormGroup, eventData: EventBackendData): number | null {

    const eventFormData: EventFormData = {
      idevent: eventData.idevent,
      event_type: eventData.calendar_type?.code || null,
      idcourse: eventData.course?.id || null,
      title: eventData.title || null,
      description: eventData.description || null,
      internal: eventData.internal || 0,
      date_start: eventData.date_start || null,
      date_end: eventData.date_end || null,
      idroom: eventData.room?.id || null,
      isallday: eventData.isallday || 0,
      time_start: eventData.time_start || null,
      time_end: eventData.time_end || null,
      customer: eventData.customer || null,
      idcontact: eventData.customer?.contact?.id || null,
      customer_headquarter: eventData.customer?.headquarter?.id || null,
      internal_headquarter: eventData.company_location?.id || null,
      keepinformed: eventData.users?.map(user => user.id) || null,
      customerstudents: eventData.customerstudents?.map(student => student.id) || null
    }

    // Prima imposta i valori base
    form.patchValue(eventFormData);

    // Poi applica tutta la logica necessaria
    setTimeout(() => {
      // this.applyEventTypeLogic(form, eventFormData.event_type);
      // this.applyDateTypeLogic(form, eventFormData.event_type);
      // this.applyIsAllDayLogic(form, eventFormData.isallday);
      // this.applyInternalLogic(form, eventFormData.internal);

      // // Se c'è un cliente, abilita i campi correlati
      // if (eventFormData.customer) {
      //   this.applyCustomerLogic(form, eventFormData.customer);
      // }
    });

    // Ritorna il valore del customer_headquarter per impostarlo dopo il caricamento delle sedi
    return eventData.customer?.headquarter?.id || null;
  }
}
